import Decimal from 'decimal.js';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { IntegrationExternalKind } from '@/types/schema';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { formatPercent } from '@/utils/formatting/percent';
import { getNodes } from '@/utils/graphqlUtils';

import {
  GetEntityAssetsForDpSelector_InsurancePolicyFragment,
  useGetEntityAssetsForDpSelectorQuery,
} from '../graphql/GetEntityAssetsForDPSelector.generated';

/**
 * Prefix used to create composite option values that encode both
 * the asset type and the ID, e.g. `assetStatic:abc123`.
 */
export enum AssetOptionPrefix {
  AssetStatic = 'assetStatic',
  AssetGroup = 'assetGroup',
  InsurancePolicy = 'insurancePolicy',
  OwnershipStake = 'ownershipStake',
}

/**
 * Single source of truth for the typeahead `groupName` strings used by the
 * asset-DP option mappers. The 2nd-death rename in `AssetSelectorDropdown`
 * keys off these values, so they must agree.
 */
export const ASSET_OPTION_GROUP_NAMES = {
  BUSINESSES: 'Businesses',
  ASSETS: 'Assets',
  LIFE_INSURANCE: 'Life insurance',
} as const;

/** Parse a composite option value back into its type and ID. */
export function parseAssetOptionValue(compositeValue: string): {
  prefix: AssetOptionPrefix;
  id: string;
} {
  const [prefix, ...idParts] = compositeValue.split(':');
  return {
    prefix: prefix as AssetOptionPrefix,
    id: idParts.join(':'),
  };
}

/** Build a composite option value from a prefix and ID. */
function makeCompositeValue(prefix: AssetOptionPrefix, id: string): string {
  return `${prefix}:${id}`;
}

interface OwnershipStakeOption {
  id: string;
  ownershipPercentage?: Decimal | null;
  ownedEntity?: {
    id: string;
    subtype: { id: string; displayName: string };
  } | null;
}

export function mapStakeToOption(
  stake: OwnershipStakeOption
): TypeaheadSelectInputOption<string> {
  const entityName = (
    stake.ownedEntity?.subtype?.displayName ?? 'Unknown entity'
  ).trim();
  const pct = stake.ownershipPercentage
    ? new Decimal(stake.ownershipPercentage)
    : null;
  // Bump precision for sub-1% stakes so a grantor's 0.1% sliver doesn't
  // render as "0% ownership". ownershipPercentage is stored in 0–100 form
  // (50 = 50%), so the threshold is 1, not 0.01.
  const precision = pct && !pct.isZero() && pct.lt(1) ? 2 : 0;
  const ownershipPct = pct
    ? `${formatPercent(pct, precision)}% ownership`
    : undefined;
  return {
    display: entityName,
    value: makeCompositeValue(AssetOptionPrefix.OwnershipStake, stake.id),
    groupName: ASSET_OPTION_GROUP_NAMES.BUSINESSES,
    endLabel: ownershipPct,
  };
}

export function mapPolicyToOption(
  policy: GetEntityAssetsForDpSelector_InsurancePolicyFragment
): TypeaheadSelectInputOption<string> {
  const name = (policy.carrierName || 'Life insurance').trim();
  const deathBenefit = formatCurrencyNoDecimals(
    new Decimal(policy.deathBenefitAmount)
  );
  const deathBenefitLabel = `${deathBenefit} death benefit`;
  // Keep the death-benefit label in `endLabel` only — the inline dropdown
  // and the cross-entity drawer both render `endLabel` separately, so
  // baking it into `display` would duplicate it (e.g. the drawer would
  // render "Lincoln National ($1M death benefit) ($1M death benefit)").
  // Prose consumers (accordion header, callout) should use
  // `getAssetOptionProseLabel` to recover the death-benefit context.
  return {
    display: name,
    value: makeCompositeValue(AssetOptionPrefix.InsurancePolicy, policy.id),
    groupName: ASSET_OPTION_GROUP_NAMES.LIFE_INSURANCE,
    endLabel: deathBenefitLabel,
  };
}

/**
 * Returns a user-facing label suitable for prose (e.g. accordion headers,
 * info callouts) where `endLabel` is not rendered as a separate UI slot.
 *
 * Only insurance policies fold `endLabel` (death benefit) into the label —
 * for other asset types, the qualifier (ownership %, asset value) was
 * historically not shown in these contexts and would read awkwardly inline.
 */
export function getAssetOptionProseLabel(
  option: TypeaheadSelectInputOption<string>
): string {
  const { prefix } = parseAssetOptionValue(option.value);
  if (prefix === AssetOptionPrefix.InsurancePolicy && option.endLabel) {
    return `${option.display} (${option.endLabel})`;
  }
  return option.display;
}

interface AssetStaticGroupOption {
  id: string;
  name: string;
  integrationKind?: IntegrationExternalKind | null;
}

interface AssetStaticOption {
  id: string;
  name: string;
  currentValue?: { ownedValue?: Decimal | null } | null;
  /**
   * The asset groups this static belongs to. An integrated/linked account is
   * itself an AssetGroup (carries an `integrationKind`); that group is the
   * parent we nest the static under.
   */
  groups?: Array<AssetStaticGroupOption> | null;
  /**
   * Underlying holdings. `accountName` identifies the integrated/linked account
   * the static belongs to (matched against the account's AssetGroup).
   */
  assets?: Array<{
    accountName?: string | null;
  }> | null;
}

/**
 * Maps an AssetStatic (from `Entity.currentHoldings.assets` /
 * `Household.entityHoldings`) to a typeahead option. AssetStatic IDs are the
 * de-duping key used elsewhere in the form, so the static's `id` is what we
 * encode into the composite option value.
 */
export function mapAssetStaticToOption(
  node: AssetStaticOption
): TypeaheadSelectInputOption<string> {
  const ownedValue = node.currentValue?.ownedValue;
  const valueLabel =
    ownedValue != null && new Decimal(ownedValue).greaterThan(0)
      ? formatCurrencyNoDecimals(new Decimal(ownedValue))
      : undefined;
  return {
    display: node.name.trim(),
    value: makeCompositeValue(AssetOptionPrefix.AssetStatic, node.id),
    groupName: ASSET_OPTION_GROUP_NAMES.ASSETS,
    endLabel: valueLabel,
  };
}

interface AssetGroupOption {
  id: string;
  name: string;
}

export function mapAssetGroupToOption(
  node: AssetGroupOption
): TypeaheadSelectInputOption<string> {
  return {
    display: node.name.trim(),
    value: makeCompositeValue(AssetOptionPrefix.AssetGroup, node.id),
    groupName: ASSET_OPTION_GROUP_NAMES.ASSETS,
  };
}

/** The integrated/linked account name a static is held in, if any. */
function getAssetStaticAccountName(
  node: AssetStaticOption
): string | undefined {
  for (const asset of node.assets ?? []) {
    const name = asset.accountName?.trim();
    if (name) return name;
  }
  return undefined;
}

/**
 * Returns the integrated/linked holding account (an AssetGroup carrying an
 * `integrationKind`) a static belongs to, or null for manual / non-integrated
 * statics. A static can belong to several integrated groups — e.g. its
 * custodian holding account *and* a broader legal-entity/trust group — so we
 * match on the holding account's name (the AssetV2 `accountName`) to nest at
 * the account level the holdings table uses, not the trust level.
 */
export function getAssetStaticIntegratedGroup(
  node: AssetStaticOption
): AssetStaticGroupOption | null {
  const accountName = getAssetStaticAccountName(node);
  if (!accountName) return null;
  for (const group of node.groups ?? []) {
    if (group.integrationKind != null && group.name.trim() === accountName) {
      return group;
    }
  }
  return null;
}

/**
 * Whether all, some, or none of a parent row's children are selected. Used to
 * derive the parent checkbox state (checked / indeterminate / unchecked).
 */
export function getParentSelectionState(
  parent: TypeaheadSelectInputOption<string>,
  selectedValues: Set<string>
): 'all' | 'some' | 'none' {
  const children = parent.childValues ?? [];
  if (children.length === 0) return 'none';
  const selectedCount = children.filter((c) => selectedValues.has(c)).length;
  if (selectedCount === 0) return 'none';
  if (selectedCount === children.length) return 'all';
  return 'some';
}

/**
 * Folds a flat list of AssetStatic options into account-nested rows: each
 * integrated/linked account (a real AssetGroup) becomes a single checkable
 * parent row (showing the account's total value) immediately followed by its
 * holdings as indented child rows. The parent's value is the account's
 * `assetGroup:<id>` — selecting it persists the group, which the waterfall
 * resolves to the account's current members. Manual (non-integrated) statics
 * stay flat. Accounts are ordered by total value descending, holdings within
 * an account by value descending, and manual statics after the accounts by
 * value descending.
 */
export function groupAssetStaticOptions(
  statics: AssetStaticOption[]
): TypeaheadSelectInputOption<string>[] {
  interface AccountGroup {
    group: AssetStaticGroupOption;
    children: Array<{
      option: TypeaheadSelectInputOption<string>;
      sort: number;
    }>;
    total: Decimal;
  }

  const accountOrder: string[] = [];
  const byGroup = new Map<string, AccountGroup>();
  const manual: Array<{
    option: TypeaheadSelectInputOption<string>;
    sort: number;
  }> = [];

  for (const node of statics) {
    const baseOption = mapAssetStaticToOption(node);
    const ownedValue = node.currentValue?.ownedValue;
    const sort = ownedValue != null ? new Decimal(ownedValue).toNumber() : 0;
    const integratedGroup = getAssetStaticIntegratedGroup(node);

    if (!integratedGroup) {
      manual.push({ option: baseOption, sort });
      continue;
    }

    if (!byGroup.has(integratedGroup.id)) {
      byGroup.set(integratedGroup.id, {
        group: integratedGroup,
        children: [],
        total: new Decimal(0),
      });
      accountOrder.push(integratedGroup.id);
    }
    const account = byGroup.get(integratedGroup.id);
    // account is always present: we just set it above if it was missing.
    if (!account) continue;
    const parentValue = makeCompositeValue(
      AssetOptionPrefix.AssetGroup,
      integratedGroup.id
    );
    // Use the static's own name. When an entity does not group integration
    // asset values, each holding has its own static (e.g. "Apple"), and
    // holdings in the same account and asset class must stay distinct.
    account.children.push({
      option: {
        ...baseOption,
        display: node.name.trim(),
        indentLevel: 1,
        parentValue,
      },
      sort,
    });
    if (ownedValue != null) {
      account.total = account.total.plus(new Decimal(ownedValue));
    }
  }

  const orderedGroups = accountOrder
    .map((id) => byGroup.get(id))
    .filter((g): g is AccountGroup => g != null)
    .sort((a, b) => b.total.comparedTo(a.total));

  const result: TypeaheadSelectInputOption<string>[] = [];
  for (const account of orderedGroups) {
    account.children.sort((a, b) => b.sort - a.sort);
    const childValues = account.children.map((c) => c.option.value);
    result.push({
      display: account.group.name.trim(),
      value: makeCompositeValue(AssetOptionPrefix.AssetGroup, account.group.id),
      groupName: ASSET_OPTION_GROUP_NAMES.ASSETS,
      endLabel: account.total.greaterThan(0)
        ? formatCurrencyNoDecimals(account.total)
        : undefined,
      isParentRow: true,
      indentLevel: 0,
      childValues,
    });
    result.push(...account.children.map((c) => c.option));
  }

  manual.sort((a, b) => b.sort - a.sort);
  result.push(...manual.map((m) => m.option));

  return result;
}

interface UseEntityAssetOptionsResult {
  options: TypeaheadSelectInputOption<string>[];
  /**
   * Display name of the entity or client profile that holds the assets. It is
   * null until the query resolves.
   */
  holderDisplayName: string | null;
  loading: boolean;
}

/**
 * Fetches all assets for an entity and returns them as grouped
 * TypeaheadSelectInputOption[], suitable for use with TypeaheadMultiSelectInput.
 *
 * Options use composite values (e.g. `assetStatic:abc123`) so the caller
 * can distinguish asset types from a flat value list.
 *
 * `groupByIntegratedAccount` folds an integrated/linked account's statics into
 * a checkable parent row (the account's `assetGroup:<id>`) + nested holding
 * rows, and omits the standalone asset-group rows (the accounts are now the
 * parents). It's opt-in because the consuming `AssetSelectorDropdown` wires up
 * the parent cascade and the group<->statics read/write conversion; other
 * callers (e.g. the split modals, the recipient-card name resolver) keep the
 * default flat list.
 */
export function useEntityAssetOptions(
  entityId: string | null,
  {
    groupByIntegratedAccount = false,
  }: { groupByIntegratedAccount?: boolean } = {}
): UseEntityAssetOptionsResult {
  const { data, loading } = useGetEntityAssetsForDpSelectorQuery({
    variables: { entityId: entityId ?? '' },
    skip: !entityId,
    fetchPolicy: 'cache-and-network',
  });

  const options = useMemo(() => {
    const node = data?.node;
    if (!node) return [];

    if (node.__typename === 'ClientProfile') {
      return compact((node.ownedOwnershipStakes ?? []).map(mapStakeToOption));
    }

    const entity = node.__typename === 'Entity' ? node : null;
    if (!entity) return [];

    const holdings = entity.currentHoldings;
    const result: TypeaheadSelectInputOption<string>[] = [];

    // --- Businesses (ownership stakes) ---
    result.push(...holdings.ownershipStakes.map(mapStakeToOption));

    // --- Life insurance (policies held by the entity) ---
    result.push(...holdings.insurancePolicies.map(mapPolicyToOption));

    // --- Assets from the entity's current valuation. When opted in, integrated
    //     accounts are folded into nested parent + holding rows; otherwise each
    //     static is a flat option. ---
    result.push(
      ...(groupByIntegratedAccount
        ? groupAssetStaticOptions(holdings.assets)
        : holdings.assets.map(mapAssetStaticToOption))
    );

    // --- Asset groups (not part of EntityHoldings; fetched alongside). In
    //     grouped mode, integrated accounts are already the nested parent rows
    //     and the entity's own legal-entity container group is just the entity
    //     itself, so we list only manual (non-integrated) groups — keeping them
    //     selectable without duplicating accounts. The flat list keeps every
    //     group. ---
    const assetGroupNodes = getNodes(entity.assetGroups);
    result.push(
      ...(groupByIntegratedAccount
        ? assetGroupNodes.filter((group) => group.integrationKind == null)
        : assetGroupNodes
      ).map(mapAssetGroupToOption)
    );

    return compact(result);
  }, [data, groupByIntegratedAccount]);

  let holderDisplayName: string | null = null;
  if (data?.node?.__typename === 'Entity') {
    holderDisplayName = data.node.subtype.displayName;
  } else if (data?.node?.__typename === 'ClientProfile') {
    holderDisplayName = data.node.displayName;
  }

  return { options, holderDisplayName, loading };
}

/**
 * Given the 4 typed ID arrays from the form, build an array of composite
 * option values. Inverse of {@link distributeAssetOptionValues}.
 */
export function buildCompositeAssetValues(ids: {
  assetStaticIds?: string[];
  assetGroupIds?: string[];
  insurancePolicyIds?: string[];
  ownershipStakeIds?: string[];
}): string[] {
  const values: string[] = [];
  for (const id of ids.assetStaticIds ?? []) {
    values.push(makeCompositeValue(AssetOptionPrefix.AssetStatic, id));
  }
  for (const id of ids.assetGroupIds ?? []) {
    values.push(makeCompositeValue(AssetOptionPrefix.AssetGroup, id));
  }
  for (const id of ids.insurancePolicyIds ?? []) {
    values.push(makeCompositeValue(AssetOptionPrefix.InsurancePolicy, id));
  }
  for (const id of ids.ownershipStakeIds ?? []) {
    values.push(makeCompositeValue(AssetOptionPrefix.OwnershipStake, id));
  }
  return values;
}

/**
 * Given an array of composite option values, distribute them into the
 * 4 typed ID arrays used by the form.
 */
export function distributeAssetOptionValues(compositeValues: string[]): {
  assetStaticIds: string[];
  assetGroupIds: string[];
  insurancePolicyIds: string[];
  ownershipStakeIds: string[];
} {
  const assetStaticIds: string[] = [];
  const assetGroupIds: string[] = [];
  const insurancePolicyIds: string[] = [];
  const ownershipStakeIds: string[] = [];

  for (const v of compositeValues) {
    const { prefix, id } = parseAssetOptionValue(v);
    switch (prefix) {
      case AssetOptionPrefix.AssetStatic:
        assetStaticIds.push(id);
        break;
      case AssetOptionPrefix.AssetGroup:
        assetGroupIds.push(id);
        break;
      case AssetOptionPrefix.InsurancePolicy:
        insurancePolicyIds.push(id);
        break;
      case AssetOptionPrefix.OwnershipStake:
        ownershipStakeIds.push(id);
        break;
    }
  }

  return {
    assetStaticIds,
    assetGroupIds,
    insurancePolicyIds,
    ownershipStakeIds,
  };
}
